import {CalendarOutlined, CheckCircleOutlined, DeleteOutlined, PlusOutlined} from '@ant-design/icons';
import {Button, Descriptions, Drawer, Empty, Form, Modal, Popconfirm, Space, Spin, Tag, message} from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import {useMemo, useState} from 'react';

import {RQDeleteFollowUpAPI, RQFollowUpAddAPI, RQFollowUpCompleteAPI, RQFollowUpLookupByPatientAPI} from '../../../../../../ReactQuery/apis/rqFollowUpAPI';
import {AddFollowUp} from '../AddFollowUp';

// ─── Status helper ───────────────────────────────────────────────────────────
const getFollowUpStatus = record => {
  if (record.status === 'COMPLETED') {
    return {label: 'Completed', color: 'green'};
  }

  if (record.nextVisit && dayjs(record.nextVisit).isBefore(dayjs(), 'day')) {
    return {label: 'Overdue', color: 'red'};
  }

  return {label: record.status || 'Pending', color: 'gold'};
};

// ─── Follow Up Details Drawer (scoped to a single prescription) ────────────
const FollowUpDetails = ({open, onClose, prescription, patientId, token}) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [form] = Form.useForm();

  const prescriptionId = prescription?.prescriptionId;

  const {data: followUps = [], isFetching: loading, refetch} = RQFollowUpLookupByPatientAPI(patientId, token, {enabled: !!token && !!patientId && open});

  // Follow ups are added against a prescription, so find the one tied to this prescription
  const followUp = useMemo(
    () => followUps.find(f => f.prescriptionId === prescriptionId),
    [followUps, prescriptionId],
  );

  const addFollowUp = RQFollowUpAddAPI({
    onSuccess: () => {
      message.success('Follow up scheduled successfully');
      refetch();
      closeFormModal();
    },
    onError: err => message.error(err?.response?.data?.message || err?.message || 'Failed to schedule follow up'),
  });

  const completeFollowUp = RQFollowUpCompleteAPI({
    onSuccess: () => {
      message.success('Follow up marked as completed');
      refetch();
    },
    onError: err => message.error(err?.response?.data?.message || err?.message || 'Failed to complete follow up'),
    onSettled: () => setPendingAction(null),
  });

  const deleteFollowUp = RQDeleteFollowUpAPI({
    onSuccess: () => {
      message.success('Follow up deleted successfully');
      refetch();
    },
    onError: err => message.error(err?.response?.data?.message || err?.message || 'Failed to delete follow up'),
    onSettled: () => setPendingAction(null),
  });

  const openAddModal = () => {
    setIsFormOpen(true);
    setTimeout(() => form.setFieldsValue({prescriptionId}), 0);
  };

  const closeFormModal = () => {
    setIsFormOpen(false);
    form.resetFields();
  };

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then(values => {
        if (!token) {
          message.error('You are not logged in. Please log in again.');

          return;
        }

        const payload = {
          prescriptionId: values.prescriptionId,
          followUpDays: values.followUpDays,
          remarks: values.remarks,
        };

        addFollowUp.mutate({formData: payload, token});
      })
      .catch(() => message.error('Please fill all required fields correctly.'));
  };

  const handleComplete = () => {
    setPendingAction('complete');
    completeFollowUp.mutate({id: followUp.id, formData: {}, token});
  };

  const handleDelete = () => {
    setPendingAction('delete');
    deleteFollowUp.mutate({id: followUp.id, token});
  };

  const status = followUp ? getFollowUpStatus(followUp) : null;
  const isCompleted = followUp?.status === 'COMPLETED';

  return (
    <Modal
      title={(
        <div className={'flex items-center gap-2'}>
          <CalendarOutlined className={'text-primary-600'} />
          <span className={'font-semibold'}>Follow Up Details</span>
        </div>
      )}
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
      destroyOnHidden
      centered
      maskClosable={false}
    >
      {loading ? (
        <div className={'flex justify-center py-10'}><Spin /></div>
      ) : followUp ? (
        <div>
          <Descriptions column={1} bordered size={'small'} className={'mb-4'}>
            <Descriptions.Item label={'Status'}>
              <Tag color={status.color}>{status.label}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={'Previous Visit'}>{followUp.previousVisit || '—'}</Descriptions.Item>
            <Descriptions.Item label={'Follow Up Days'}>{followUp.followUpDays ?? '—'}</Descriptions.Item>
            <Descriptions.Item label={'Next Visit'}>{followUp.nextVisit || '—'}</Descriptions.Item>
            <Descriptions.Item label={'Doctor'}>{followUp.doctorName || '—'}</Descriptions.Item>
            <Descriptions.Item label={'Remarks'}>{followUp.remarks || '—'}</Descriptions.Item>
          </Descriptions>

          <Space className={'mt-4'}>
            {!isCompleted && (
              <Popconfirm
                title={'Mark this follow up as completed?'}
                onConfirm={handleComplete}
                okText={'Complete'}
              >
                <Button
                  icon={<CheckCircleOutlined />}
                  loading={pendingAction === 'complete' && completeFollowUp.isPending}
                  disabled={pendingAction === 'delete'}
                  className={'btn-primary'}
                >
                  Mark Completed
                </Button>
              </Popconfirm>
            )}
            <Popconfirm
              title={'Delete this follow up?'}
              description={'This action cannot be undone.'}
              onConfirm={handleDelete}
              okButtonProps={{danger: true}}
              okText={'Delete'}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={pendingAction === 'delete' && deleteFollowUp.isPending}
                disabled={pendingAction === 'complete'}
              >
                Delete
              </Button>
            </Popconfirm>
          </Space>
        </div>
      ) : (
        <Empty description={'No follow up scheduled for this prescription yet'}>
          <Button type={'primary'} icon={<PlusOutlined />} onClick={openAddModal} className={'btn-primary'}>
            Add Follow Up
          </Button>
        </Empty>
      )}

      <AddFollowUp
        open={isFormOpen}
        prescriptionId={prescriptionId}
        confirmLoading={addFollowUp.isPending}
        onCancel={closeFormModal}
        onSubmit={handleFormSubmit}
        form={form}
      />
    </Modal>
  );
};

FollowUpDetails.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  prescription: PropTypes.object,
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  token: PropTypes.string,
};

export default FollowUpDetails;
